import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { useCruises } from '../context/CruiseContext';
import { 
  ChevronLeft, 
  ExternalLink,
  Navigation,
  MapPin,
  Ship,
  Calendar,
  Ruler,
  Gauge,
  Users,
  Home,
  Wrench,
  Flag,
  Anchor,
  Briefcase,
  Info,
  UserCircle,
  Weight,
  ChevronRight
} from 'lucide-react';
import { parseISO, startOfDay } from 'date-fns';
import { motion } from 'framer-motion';
import LazyImage from '../components/LazyImage';
import CustomCruiseIcon from '../components/CustomCruiseIcon';
import { ShipDetailsSkeleton } from '../components/LoadingSkeleton';
import { getCountryCodeFromFlagState } from '../utils/flagUtils';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';

const ShipDetails = () => {
  const { cruiseLine, shipName } = useParams<{ cruiseLine: string; shipName: string }>();
  const navigate = useNavigate();
  const { getShipImageUrl, getShipLocationUrl, getShipDeckplanUrl, getShipMetadata, loading: metadataLoading } = useCruiseMetadata();
  const { cruises } = useCruises();
  const { formatTonnage, formatDimensions, formatSpeed, formatNumber, formatDate } = useDisplayPreferences();
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [shipImageError, setShipImageError] = useState(false);

  // Decode URL parameters
  const decodedCruiseLine = cruiseLine ? decodeURIComponent(cruiseLine) : '';
  const decodedShipName = shipName ? decodeURIComponent(shipName) : '';

  // Get ship metadata
  const shipMetadata = decodedCruiseLine && decodedShipName 
    ? getShipMetadata(decodedCruiseLine, decodedShipName) 
    : null;
  const shipImageUrl = decodedCruiseLine && decodedShipName 
    ? getShipImageUrl(decodedCruiseLine, decodedShipName) 
    : null;
  const shipLocationUrl = decodedCruiseLine && decodedShipName 
    ? getShipLocationUrl(decodedCruiseLine, decodedShipName) 
    : null;
  const shipDeckplanUrl = decodedCruiseLine && decodedShipName 
    ? getShipDeckplanUrl(decodedCruiseLine, decodedShipName) 
    : null;

  // Ensure skeleton shows for minimum duration
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Track when data is actually loaded
  useEffect(() => {
    if (!metadataLoading) {
      setDataLoaded(true);
      setLoading(false);
    } else {
      setDataLoaded(false);
      setLoading(true);
    }
  }, [metadataLoading]);

  const handleBackNavigation = () => {
    navigate(-1);
  };

  // Get flag country code
  const flagCountryCode = shipMetadata?.flagState 
    ? getCountryCodeFromFlagState(shipMetadata.flagState) 
    : null;

  // User's sailings on this ship (case-insensitive match)
  const shipCruises = (cruises || []).filter(
    (c) =>
      c.shipName?.trim().toLowerCase() === decodedShipName?.trim().toLowerCase() &&
      c.cruiseLine?.trim().toLowerCase() === decodedCruiseLine?.trim().toLowerCase()
  );
  const today = startOfDay(new Date());
  const upcomingShipCruises = shipCruises.filter((c) => startOfDay(parseISO(c.departureDate)) > today);
  const pastShipCruises = shipCruises
    .filter((c) => startOfDay(parseISO(c.returnDate)) <= today)
    .sort((a, b) => parseISO(b.departureDate).getTime() - parseISO(a.departureDate).getTime());

  // Show skeleton until both data is loaded AND minimum time has elapsed
  if (!dataLoaded || !minTimeElapsed || loading) {
    return <ShipDetailsSkeleton />;
  }

  // Handle case where ship metadata is not found
  if (!decodedCruiseLine || !decodedShipName) {
    return (
      <div className="space-y-6">
        <div className="flex items-center">
          <button 
            onClick={handleBackNavigation}
            className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="Go back"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Ship Not Found</h1>
            <p className="text-gray-600">The ship you're looking for could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: dataLoaded ? 1 : 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <div className="mb-6 flex items-center">
        <button 
          onClick={handleBackNavigation}
          className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
          aria-label="Go back"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-800">{decodedShipName}</h1>
          <p className="text-gray-600">{decodedCruiseLine}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Photo Header */}
        <div className="h-80 bg-gradient-to-r from-blue-900 to-blue-700 relative">
          {shipImageUrl && !shipImageError ? (
            <>
              <LazyImage 
                src={shipImageUrl} 
                alt={`${decodedShipName} - ${decodedCruiseLine}`}
                className="w-full h-full object-cover"
                onError={() => {
                  // Mark image as failed so we can show fallback
                  setShipImageError(true);
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 h-2/3 bg-gradient-to-t from-black/60 to-transparent"></div>
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <CustomCruiseIcon size={160} className="text-white/30" />
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent text-white">
            <h2 className="text-2xl font-bold">{decodedShipName}</h2>
            <p className="text-lg opacity-90">{decodedCruiseLine}</p>
          </div>
        </div>

        <div className="p-6">
          {/* Primary Info Section */}
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Ship className="h-5 w-5 text-secondary-500 mr-2" />
              Ship Information
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {shipMetadata?.shipClass && (
                <div className="flex items-start space-x-3">
                  <Anchor className="h-5 w-5 text-secondary-500 mt-0.5 shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900">Ship Class</h4>
                    <p className="text-gray-700">{shipMetadata.shipClass}</p>
                  </div>
                </div>
              )}
              {shipMetadata?.yearBuilt && (
                <div className="flex items-start space-x-3">
                  <Calendar className="h-5 w-5 text-secondary-500 mt-0.5 shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900">Year Built</h4>
                    <p className="text-gray-700">{shipMetadata.yearBuilt}</p>
                    {shipMetadata.lastRefurbishment && (
                      <p className="text-sm text-gray-600 mt-1">
                        Last Refurbished: {shipMetadata.lastRefurbishment}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {shipMetadata?.shipClass && (
                <div className="flex items-start sm:items-center">
                  <Link 
                    to={`/ship-class/${encodeURIComponent((shipMetadata.normalizedClass?.trim() || shipMetadata.shipClass?.trim() || ''))}`}
                    className="inline-flex items-center px-3 py-1.5 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition-colors text-sm font-medium"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    View all in class
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Specifications Section */}
          {(shipMetadata?.grossTonnage || shipMetadata?.length || shipMetadata?.width || shipMetadata?.speed || shipMetadata?.decks) && (
            <div className="mb-6 pt-6 border-t border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Ruler className="h-5 w-5 text-secondary-500 mr-2" />
                Specifications
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {shipMetadata.grossTonnage && (
                  <div className="flex items-start space-x-3">
                    <Weight className="h-5 w-5 text-secondary-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Gross Tonnage</h4>
                      <p className="text-gray-700">{formatTonnage(shipMetadata.grossTonnage)}</p>
                    </div>
                  </div>
                )}
                {shipMetadata.decks && (
                  <div className="flex items-start space-x-3">
                    <Home className="h-5 w-5 text-secondary-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Decks</h4>
                      <p className="text-gray-700">{formatNumber(shipMetadata.decks)}</p>
                    </div>
                  </div>
                )}
                {shipMetadata.speed && (
                  <div className="flex items-start space-x-3">
                    <Gauge className="h-5 w-5 text-secondary-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Speed</h4>
                      <p className="text-gray-700">{formatSpeed(shipMetadata.speed)}</p>
                    </div>
                  </div>
                )}
                {(shipMetadata.length || shipMetadata.width) && (
                  <div className="flex items-start space-x-3">
                    <Ruler className="h-5 w-5 text-secondary-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Dimensions</h4>
                      <p className="text-gray-700">
                        {formatDimensions(shipMetadata.length, shipMetadata.width)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Capacity Section */}
          {(shipMetadata?.passengers || shipMetadata?.crew || shipMetadata?.cabin) && (
            <div className="mb-6 pt-6 border-t border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <UserCircle className="h-5 w-5 text-secondary-500 mr-2" />
                Capacity
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {shipMetadata.passengers && (
                  <div className="flex items-start space-x-3">
                    <Users className="h-5 w-5 text-secondary-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Passengers</h4>
                      <p className="text-gray-700">{formatNumber(shipMetadata.passengers)}</p>
                    </div>
                  </div>
                )}
                {shipMetadata.crew && (
                  <div className="flex items-start space-x-3">
                    <Briefcase className="h-5 w-5 text-secondary-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Crew</h4>
                      <p className="text-gray-700">{formatNumber(shipMetadata.crew)}</p>
                    </div>
                  </div>
                )}
                {shipMetadata.cabin && (
                  <div className="flex items-start space-x-3">
                    <Home className="h-5 w-5 text-secondary-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Cabins</h4>
                      <p className="text-gray-700">{formatNumber(shipMetadata.cabin)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Info Section */}
          {(shipMetadata?.builder || shipMetadata?.flagState) && (
            <div className="mb-6 pt-6 border-t border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Info className="h-5 w-5 text-secondary-500 mr-2" />
                Additional Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {shipMetadata.builder && (
                  <div className="flex items-start space-x-3">
                    <Wrench className="h-5 w-5 text-secondary-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Builder</h4>
                      <p className="text-gray-700">{shipMetadata.builder}</p>
                    </div>
                  </div>
                )}
                {shipMetadata.flagState && (
                  <div className="flex items-start space-x-3">
                    <Flag className="h-5 w-5 text-secondary-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Flag State</h4>
                      <div className="flex items-center space-x-2">
                        {flagCountryCode && (
                          <span className={`fi fi-${flagCountryCode} text-2xl`}></span>
                        )}
                        <p className="text-gray-700">{shipMetadata.flagState}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* External Actions Section */}
          {(shipLocationUrl || shipDeckplanUrl) && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                <ExternalLink className="h-5 w-5 text-secondary-500 mr-2" />
                External Actions
              </h3>
              <div className="flex flex-wrap gap-3">
                {shipLocationUrl && (
                  <a 
                    href={shipLocationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 transition-colors font-medium"
                  >
                    <Navigation className="h-4 w-4 mr-2" />
                    Track Ship
                  </a>
                )}
                {shipDeckplanUrl && (
                  <a 
                    href={shipDeckplanUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 transition-colors font-medium"
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    View Deck Plan
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Your sailings on this ship */}
          {shipCruises.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="h-5 w-5 text-secondary-500 mr-2" />
                Your sailings on this ship
              </h3>

              {upcomingShipCruises.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">Upcoming</h4>
                  <ul className="space-y-1">
                    {upcomingShipCruises.map((cruise) => {
                      const href = `/cruise/${cruise.id}`;
                      return (
                        <li key={cruise.id}>
                          <Link
                            to={href}
                            className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-gray-50 text-gray-900 group"
                          >
                            <span className="text-gray-700">
                              {formatDate(parseISO(cruise.departureDate))} – {formatDate(parseISO(cruise.returnDate))}
                            </span>
                            <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-secondary-500 shrink-0" />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {pastShipCruises.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">Past</h4>
                  <ul className="space-y-1">
                    {pastShipCruises.map((cruise) => {
                      const needsCompletion = !cruise.completed;
                      const href = needsCompletion ? `/post-cruise-details/${cruise.id}` : `/cruise/${cruise.id}`;
                      return (
                        <li key={cruise.id}>
                          <Link
                            to={href}
                            className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-gray-50 text-gray-900 group"
                          >
                            <span className="text-gray-700">
                              {formatDate(parseISO(cruise.departureDate))} – {formatDate(parseISO(cruise.returnDate))}
                            </span>
                            <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-secondary-500 shrink-0" />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ShipDetails;

